import {
    Buses,
    Capacity_Costs,
    Line_Capacity_Costs,
    Maybe,
    Model_Lines,
    Model_Lines_To_Missing_Buses,
    Unlocked_Buses
} from "generated/graphql";
import {
    assertMaybeToValue,
    convertMaybeToUndefined
} from "graphql/helpers/queryHelpers";
import { hasuraBusToNiraBus } from "./busType";
import { RegionType } from "./regionType";
import { isKnownVoltageType } from "./voltageTypes";

export type ConnectedLine = {
    readonly facilityId: string;
    readonly lineLength: number;
    readonly toBus: ConnectedLineBusDetails;
    readonly energySize: number;
    readonly capacitySize: number;
    readonly chargingSize: number;
    readonly allocatedCosts: number;
    readonly totalCosts: number;
};

export type ConnectedLineBusDetails = {
    readonly busId: string;
    readonly niraBusId: string;
    readonly busName: string;
    readonly latitude: number;
    readonly longitude: number;
    readonly owner: string;
};

export type LineWithMissingBus = {
    readonly facilityId: string;
    readonly lineLength: number;
    readonly rating: number;
    readonly voltage: number;
    readonly missingBusId: string;
    readonly missingBusName: string;
    readonly requestable: boolean;
};

export type LineTapDetails = {
    readonly facilityId: string;
    readonly tapName: string;
    readonly rating: number;
    readonly lineLength: number;
    readonly energySize: number;
    readonly capacitySize: number;
    readonly chargingSize: number;
    readonly allocatedCost: number;
    readonly totalCost: number;
    readonly fromBus: LineTapBusDetails;
    readonly toBus: LineTapBusDetails;
};

export type LineTapBusDetails = {
    readonly busId: string;
    readonly niraBusId: string;
    readonly busName: string;
    readonly energySize: number;
    readonly capacitySize: number;
    readonly chargingSize: number;
    readonly isLocked: boolean;
};

type HasuraConnectedLineWithCapacityCosts = Readonly<
    Pick<
        Model_Lines,
        | "facility_id"
        | "line_length"
        | "rating"
        | "voltage"
        | "from_bus_id"
        | "to_bus_id"
        | "from_nira_bus_id"
        | "to_nira_bus_id"
    > & {
        from_bus?: Maybe<HasuraFromOrToBus>;
        to_bus?: Maybe<HasuraFromOrToBus>;
        line_capacity_costs: Array<HasuraLineCapacityCost>;
    }
>;

type HasuraFromOrToBus = Readonly<
    { __typename?: "buses" } & Pick<
        Buses,
        | "id"
        | "nira_bus_id"
        | "voltage"
        | "bus_display_name"
        | "latitude"
        | "longitude"
        | "owner_name"
    >
>;

type HasuraLineCapacityCost = Readonly<
    { __typename?: "line_capacity_costs" } & Pick<
        Line_Capacity_Costs,
        | "energy_size"
        | "capacity_size"
        | "charging_size"
        | "upgrade_costs"
        | "total_upgrade_costs"
    >
>;

type HasuraLineWithMissingBus = Readonly<
    { __typename?: "model_lines_to_missing_buses" } & Pick<
        Model_Lines_To_Missing_Buses,
        | "facility_id"
        | "line_length"
        | "rating"
        | "voltage"
        | "missing_bus_id"
        | "queued_projects_at_missing_bus"
    >
>;

type HasuraBusCapacityCosts = Readonly<
    { __typename?: "capacity_costs" } & Pick<
        Capacity_Costs,
        "energy_size" | "capacity_size" | "charging_size"
    >
>;

type HasuraUnlockedBus = Readonly<
    { __typename?: "unlocked_buses" } & Pick<Unlocked_Buses, "unlocked">
>;

type HasuraBusWithCapacityCosts = Readonly<
    { __typename?: "buses" } & Pick<
        Buses,
        "id" | "nira_bus_id" | "voltage" | "bus_display_name"
    > & {
            capacity_costs: Array<HasuraBusCapacityCosts>;
        } & { unlocked: Array<HasuraUnlockedBus> }
>;

type HasuraLineTapDetailsWithCapacityCosts = Readonly<
    { __typename?: "model_lines" } & Pick<
        Model_Lines,
        "facility_id" | "rating" | "line_length"
    > & {
            from_bus?: Maybe<HasuraBusWithCapacityCosts>;
            to_bus?: Maybe<HasuraBusWithCapacityCosts>;
            line_capacity_costs: Array<HasuraLineCapacityCost>;
        }
>;

export const convertHasuraConnectedLinesToNiraConnectedLines = (
    busNumber: string,
    hasuraModelLines: ReadonlyArray<HasuraConnectedLineWithCapacityCosts>
): {
    connectedLines: ReadonlyArray<ConnectedLine>;
    linesWithMissingBuses: ReadonlyArray<LineWithMissingBus>;
} => {
    // we filter out lines that do not have both from_bus and to_bus defined
    // and then we have to assert to typecast to a non-null object
    const connectedLines = hasuraModelLines
        .filter(
            (hasuraModelLine) =>
                convertMaybeToUndefined(hasuraModelLine.from_bus) &&
                convertMaybeToUndefined(hasuraModelLine.to_bus)
        )
        // skip lines connected to a bus with an unsupported voltage instead of
        // crashing on conversion (mirrors the filter in
        // convertHasuraBusesToNiraBusesAndSplitBasedOnAuth)
        .filter((hasuraModelLine) => {
            const hasKnownVoltages =
                isKnownVoltageType(
                    assertMaybeToValue(
                        assertMaybeToValue(hasuraModelLine.from_bus).voltage
                    )
                ) &&
                isKnownVoltageType(
                    assertMaybeToValue(
                        assertMaybeToValue(hasuraModelLine.to_bus).voltage
                    )
                );
            if (!hasKnownVoltages) {
                console.warn(
                    `Skipping connected line ${hasuraModelLine.facility_id}: connected bus has an unsupported voltage`
                );
            }
            return hasKnownVoltages;
        })
        .map((hasuraModelLine) => {
            const niraFromBus = hasuraBusToNiraBus(
                assertMaybeToValue(hasuraModelLine.from_bus)
            );
            const niraToBus = hasuraBusToNiraBus(
                assertMaybeToValue(hasuraModelLine.to_bus)
            );
            return {
                facilityId: hasuraModelLine.facility_id,
                lineLength: hasuraModelLine.line_length,
                toBus:
                    busNumber == niraFromBus.id
                        ? {
                              busId: niraToBus.id,
                              niraBusId: niraToBus.niraBusId,
                              busName: niraToBus.busDisplayName,
                              latitude: niraToBus.latitude,
                              longitude: niraToBus.longitude,
                              owner: niraToBus.ownerName
                          }
                        : {
                              busId: niraFromBus.id,
                              niraBusId: niraFromBus.niraBusId,
                              busName: niraFromBus.busDisplayName,
                              latitude: niraFromBus.latitude,
                              longitude: niraFromBus.longitude,
                              owner: niraFromBus.ownerName
                          },
                energySize: hasuraModelLine.line_capacity_costs[0].energy_size,
                capacitySize:
                    hasuraModelLine.line_capacity_costs[0].capacity_size,
                chargingSize:
                    hasuraModelLine.line_capacity_costs[0].charging_size,
                allocatedCosts:
                    hasuraModelLine.line_capacity_costs[0].upgrade_costs,
                totalCosts:
                    hasuraModelLine.line_capacity_costs[0].total_upgrade_costs
            };
        });

    // we keep only lines where exactly one of from_bus or to_bus is defined
    // and we set the missing bus id to the one that is not the current bus (since that one is defined)
    const linesWithMissingBuses = hasuraModelLines
        .filter(
            (hasuraModelLine) =>
                (convertMaybeToUndefined(hasuraModelLine.from_bus) ===
                    undefined) !==
                (convertMaybeToUndefined(hasuraModelLine.to_bus) === undefined)
        )
        .map((hasuraModelLine) => {
            const missingBusId = assertMaybeToValue(
                hasuraModelLine.from_bus_id === busNumber
                    ? hasuraModelLine.to_bus_id
                    : hasuraModelLine.from_bus_id
            );
            return {
                facilityId: hasuraModelLine.facility_id,
                lineLength: hasuraModelLine.line_length,
                rating: hasuraModelLine.rating,
                voltage: hasuraModelLine.voltage,
                missingBusId: missingBusId,
                missingBusName: `Bus ${missingBusId}`,
                requestable: true
            };
        });

    return {
        connectedLines,
        linesWithMissingBuses
    };
};

export const convertHasuraLinesWithMissingBusToNiraLinesWithMissingBus = (
    hasuraLinesWithMissingBuses: ReadonlyArray<HasuraLineWithMissingBus>,
    region: RegionType
): ReadonlyArray<LineWithMissingBus> => {
    return hasuraLinesWithMissingBuses.map((hasuraLineWithMissingBus) => {
        const queuedProjectsAtMissingBus = convertMaybeToUndefined(
            hasuraLineWithMissingBus.queued_projects_at_missing_bus
        );
        return {
            facilityId: hasuraLineWithMissingBus.facility_id,
            lineLength: hasuraLineWithMissingBus.line_length,
            rating: hasuraLineWithMissingBus.rating,
            voltage: hasuraLineWithMissingBus.voltage,
            missingBusId: hasuraLineWithMissingBus.missing_bus_id,
            missingBusName:
                queuedProjectsAtMissingBus ??
                `Bus ${hasuraLineWithMissingBus.missing_bus_id}`,
            requestable: setRequestable(queuedProjectsAtMissingBus, region)
        };
    });
};

const setRequestable = (
    queuedProjectsAtMissingBus: string | undefined,
    region: RegionType
): boolean => {
    // For all regions except PJM, the model_lines_to_missing_buses table only contains lines that we could not run a study for,
    // so we can set requestable to false. For PJM, we can set requestable to true if there are no queued projects at the missing bus.
    if (region === RegionType.PJM) {
        return queuedProjectsAtMissingBus === undefined;
    } else {
        return false;
    }
};

// a line tap whose buses have unsupported voltages cannot be converted, since
// hasuraBusToNiraBus throws on unknown voltages — callers should check this
// first and fall back to an empty state
export const lineTapHasKnownVoltages = (
    hasuraLineTapDetails: HasuraLineTapDetailsWithCapacityCosts
): boolean => {
    const fromBus = convertMaybeToUndefined(hasuraLineTapDetails.from_bus);
    const toBus = convertMaybeToUndefined(hasuraLineTapDetails.to_bus);
    return (
        fromBus !== undefined &&
        toBus !== undefined &&
        isKnownVoltageType(assertMaybeToValue(fromBus.voltage)) &&
        isKnownVoltageType(assertMaybeToValue(toBus.voltage))
    );
};

export const convertHasuraLineTapDetailsToNiraLineTapDetails = (
    hasuraLineTapDetails: HasuraLineTapDetailsWithCapacityCosts
): LineTapDetails => {
    const hasuraFromBus = assertMaybeToValue(hasuraLineTapDetails.from_bus);
    const hasuraToBus = assertMaybeToValue(hasuraLineTapDetails.to_bus);
    const {
        id: fromBusId,
        niraBusId: fromNiraBusId,
        busDisplayName: fromBusDisplayName
    } = hasuraBusToNiraBus(hasuraFromBus);
    const {
        id: toBusId,
        niraBusId: toNiraBusId,
        busDisplayName: toBusDisplayName
    } = hasuraBusToNiraBus(hasuraToBus);
    return {
        facilityId: hasuraLineTapDetails.facility_id,
        tapName: `${fromBusDisplayName} --- ${toBusDisplayName}`,
        rating: hasuraLineTapDetails.rating,
        lineLength: hasuraLineTapDetails.line_length,
        energySize: hasuraLineTapDetails.line_capacity_costs[0].energy_size,
        capacitySize: hasuraLineTapDetails.line_capacity_costs[0].capacity_size,
        chargingSize: hasuraLineTapDetails.line_capacity_costs[0].charging_size,
        allocatedCost:
            hasuraLineTapDetails.line_capacity_costs[0].upgrade_costs,
        totalCost:
            hasuraLineTapDetails.line_capacity_costs[0].total_upgrade_costs,
        fromBus: {
            busId: fromBusId,
            niraBusId: fromNiraBusId,
            busName: fromBusDisplayName,
            energySize: hasuraFromBus.capacity_costs[0].energy_size,
            capacitySize: hasuraFromBus.capacity_costs[0].capacity_size,
            chargingSize: hasuraFromBus.capacity_costs[0].charging_size,
            isLocked: hasuraFromBus.unlocked.length === 0
        },
        toBus: {
            busId: toBusId,
            niraBusId: toNiraBusId,
            busName: toBusDisplayName,
            energySize: hasuraToBus.capacity_costs[0].energy_size,
            capacitySize: hasuraToBus.capacity_costs[0].capacity_size,
            chargingSize: hasuraToBus.capacity_costs[0].charging_size,
            isLocked: hasuraToBus.unlocked.length === 0
        }
    };
};
